import {
  COORDINATE_CIRCLE_LAYER_ID,
  IShapeProperties,
  SHAPE_POLYGON_FILL_LAYER_ID,
  SHAPE_POLYGON_LINE_LAYER_ID,
  SHAPE_SOURCE_ID
} from "form/map/model"
import { state } from "form/map/state"
import { IForm } from "form/model"
import { getGeoTargetsForm } from "form/tools/geo-targets"
import { Feature, Polygon } from "geojson"
import { GeoJSONSourceRaw, Map } from "mapbox-gl"
import { IGeoShape, SHAPE_TYPES } from "shape/model"
import { fitFeaturesOnMap, setSourceData } from "./common"

export function getShapesFeatures(form: IForm): Array<Feature<Polygon, IShapeProperties>> {
  const features: Array<Feature<Polygon, IShapeProperties>> = []
  for (const city of form.geoTargets.cities) {
    features.push(getFeatureFromShape(city))
  }
  for (const postalCode of form.geoTargets.postalCodes) {
    features.push(getFeatureFromShape(postalCode))
  }
  for (const stateGeoShape of form.geoTargets.states) {
    features.push(getFeatureFromShape(stateGeoShape))
  }
  for (const country of form.geoTargets.countries) {
    features.push(getFeatureFromShape(country))
  }
  return features
}

export function centerMapOnShape(shape: IGeoShape) {
  if (!state.geoTargets.map) {
    return
  }
  fitFeaturesOnMap(state.geoTargets.map, [getFeatureFromShape(shape)])
}

export function createShapeLayers(map: Map, noTempFeature?: boolean): void {
  if (map.getSource(SHAPE_SOURCE_ID)) {
    return
  }
  map.addSource(SHAPE_SOURCE_ID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: state.featuresByType.shapes
    },
    maxzoom: 24
  } as GeoJSONSourceRaw)
  map.addLayer(
    {
      id: SHAPE_POLYGON_FILL_LAYER_ID,
      type: "fill",
      source: SHAPE_SOURCE_ID,
      paint: {
        "fill-color": "#000000",
        "fill-opacity": 0.1
      }
    },
    map.getLayer(COORDINATE_CIRCLE_LAYER_ID) ? COORDINATE_CIRCLE_LAYER_ID : undefined
  )
  map.addLayer(
    {
      id: SHAPE_POLYGON_LINE_LAYER_ID,
      type: "line",
      source: SHAPE_SOURCE_ID,
      paint: {
        "line-color": "#DE8572",
        "line-width": ["case", ["==", ["get", "isTemp"], true], 1, 0]
      }
    },
    map.getLayer(COORDINATE_CIRCLE_LAYER_ID) ? COORDINATE_CIRCLE_LAYER_ID : undefined
  )
  if (noTempFeature) {
    const noTempFeatureFilter = ["==", ["get", "isTemp"], false]
    const layerIds = getGeoTargetShapeLayerIds()
    for (const layerId of layerIds) {
      map.setFilter(layerId, noTempFeatureFilter)
    }
  }
}

export function addShapesFeatures(shapes: IGeoShape[]): void {
  setSourcesFeatures([
    ...state.featuresByType.shapes.filter(f => !f.properties.isTemp),
    ...shapes.filter(s => s.type !== SHAPE_TYPES.METROPOLITAN).map(s => getFeatureFromShape(s, false))
  ])
}

export function setShapesFeaturesForType(shapes: IGeoShape[], type: SHAPE_TYPES): void {
  setSourcesFeatures([
    ...state.featuresByType.shapes.filter(f => f.properties.type !== type && !f.properties.isTemp),
    ...shapes.filter(s => s.type !== SHAPE_TYPES.METROPOLITAN).map(s => getFeatureFromShape(s, false))
  ])
}

export function deleteShapeFeatures(shapes: IGeoShape[]): void {
  const shapeIds = shapes.map(s => s.id)
  setSourcesFeatures(state.featuresByType.shapes.filter(f => !shapeIds.includes(f.properties.shape.id as number)))
}

// last feature is the temp feature
export function setTempShapeFeature(shape: IGeoShape | null): void {
  const tempFeature = !shape ? null : getFeatureFromShape(shape, true)
  const features: Array<Feature<Polygon, IShapeProperties>> = !tempFeature
    ? state.featuresByType.shapes.filter(f => !f.properties.isTemp)
    : [...state.featuresByType.shapes.filter(f => !f.properties.isTemp), tempFeature]
  setSourcesFeatures(features)
  if (state.geoTargets.map && tempFeature) {
    fitFeaturesOnMap(state.geoTargets.map, [tempFeature])
  }
}

export function setTempShapeVisibility(isVisible: boolean): void {
  const { map } = state.geoTargets
  if (!isVisible) {
    setTempShapeFeature(null)
  } else {
    const selectedShape = getGeoTargetsForm().toAddShape
    if (selectedShape) {
      setTempShapeFeature(selectedShape)
    }
  }
  if (map) {
    const newFilter = isVisible ? null : ["==", ["get", "isTemp"], false]
    const layerIds = getGeoTargetShapeLayerIds()
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) {
        map.setFilter(layerId, newFilter)
      }
    }
  }
}

export function getGeoTargetShapeLayerIds(): string[] {
  return [SHAPE_POLYGON_FILL_LAYER_ID, SHAPE_POLYGON_LINE_LAYER_ID]
}

function setSourcesFeatures(features: Array<Feature<Polygon, IShapeProperties>>): void {
  state.featuresByType.shapes = features
  if (state.geoTargets.map) {
    setSourceData(state.geoTargets.map, SHAPE_SOURCE_ID, state.featuresByType.shapes)
  }
  if (state.inventory.map) {
    setSourceData(state.inventory.map, SHAPE_SOURCE_ID, state.featuresByType.shapes)
  }
}

function getFeatureFromShape(shape: IGeoShape, isTemp?: boolean): Feature<Polygon, IShapeProperties> {
  const properties: IShapeProperties = { type: shape.type, shape: { ...shape }, isTemp: isTemp ? true : false }
  delete properties.shape.shape // remove shape because it's too big and we put it in the geometry of the feature
  return {
    id: state.nextShapeId++,
    type: "Feature",
    geometry: shape.shape,
    properties
  }
}
